import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { DeleteOutlined, PlusOutlined, SearchOutlined, ShareAltOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useToast } from '@/contexts/ToastContext'
import { useClientDetail } from '@/features/client/hooks'
import {
  useDeleteProjectStatusMutation,
  useProjectStatus,
  useUpsertProjectStatusMutation,
} from '@/hooks/useProjectStatus'
import type {
  ProjectStatusApiRow,
  ProjectStatusDeletePayload,
  ProjectStatusDraftRow,
  ProjectStatusUpsertRow,
} from '@/types/projectStatus'

type ProjectStatusPageProps = {
  clientId: string
  readOnly: boolean
  isPublic?: boolean
}

const DASHBOARD_INSIGHTS_ASSIGNEE = 'Dashboard Insights'
const EMPTY_DATE_VALUES = new Set(['0001-01-01T00:00:00', '0001-01-01T00:00:00Z'])

function normalizeDueDate(value: string | null | undefined) {
  if (!value || EMPTY_DATE_VALUES.has(value)) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

function mapProjectStatusRow(row: ProjectStatusApiRow, fallbackClientId: string, index: number): ProjectStatusDraftRow {
  const item = (row.item ?? '').trim()
  const status = row.status === 'Completed' ? 'Completed' : 'Pending'
  const clientId = row.clientId || row.clientID || fallbackClientId

  return {
    key: `${clientId}-${item || 'row'}-${index}`,
    item,
    status,
    clientId,
    assignedTo: row.assignedTo || DASHBOARD_INSIGHTS_ASSIGNEE,
    dueDate: normalizeDueDate(row.dueDate),
    isCompleted: status === 'Completed',
    isNew: false,
    oldValue: item,
  }
}

function toComparableRows(rows: ProjectStatusDraftRow[]) {
  return rows.map((row) => ({
    item: row.item.trim(),
    status: row.isCompleted ? 'Completed' : 'Pending',
    clientId: row.clientId,
    assignedTo: row.assignedTo,
    dueDate: normalizeDueDate(row.dueDate),
    isCompleted: row.isCompleted,
    isNew: row.isNew,
    oldValue: row.oldValue,
  }))
}

function buildUpsertRows(rows: ProjectStatusDraftRow[]): ProjectStatusUpsertRow[] {
  return rows.map((row) => {
    const item = row.item.trim()
    const isCompleted = row.isCompleted
    return {
      assignedTo: row.assignedTo,
      clientId: row.clientId,
      isCompleted,
      isNew: row.isNew,
      item,
      oldValue: row.oldValue,
      dueDate: normalizeDueDate(row.dueDate),
      status: isCompleted ? 'Completed' : 'Pending',
      viewOnly: false,
    }
  })
}

function calculateCompletion(rows: ProjectStatusDraftRow[]) {
  if (rows.length === 0) return 0
  const completed = rows.filter((row) => row.isCompleted).length
  return Math.round((completed / rows.length) * 100)
}

function validateRows(rows: ProjectStatusDraftRow[]) {
  const normalizedItems = rows.map((row) => row.item.trim())
  if (normalizedItems.some((item) => !item)) return false

  const seen = new Set<string>()
  for (const item of normalizedItems) {
    const key = item.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
  }

  return true
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const input = document.createElement('textarea')
  input.value = text
  input.style.position = 'fixed'
  input.style.left = '-9999px'
  document.body.appendChild(input)
  input.focus()
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

export default function ProjectStatusPage({ clientId, readOnly, isPublic = false }: ProjectStatusPageProps) {
  const { addToast } = useToast()
  const { data, isLoading, isError, refetch, isFetching } = useProjectStatus(clientId)
  const clientDetailQuery = useClientDetail(clientId, !isPublic)
  const upsertMutation = useUpsertProjectStatusMutation(clientId)
  const deleteMutation = useDeleteProjectStatusMutation(clientId)

  const serverRows = useMemo(
    () => (data?.result?.projectStatus || []).map((row, index) => mapProjectStatusRow(row, clientId, index)),
    [clientId, data?.result?.projectStatus]
  )
  const [draftRows, setDraftRows] = useState<ProjectStatusDraftRow[]>([])
  const [removedProjectStatus, setRemovedProjectStatus] = useState<string[]>([])
  const [nameFilter, setNameFilter] = useState('')
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '25', '50'],
  })
  const initializedRef = useRef(false)

  useEffect(() => {
    setDraftRows(serverRows)
    setRemovedProjectStatus([])
    initializedRef.current = true
  }, [serverRows])

  useEffect(() => {
    setPagination((current) => ({ ...current, current: 1 }))
  }, [draftRows.length, nameFilter])

  const isBusy = isLoading || (!isPublic && clientDetailQuery.isLoading) || !initializedRef.current
  const clientName = clientDetailQuery.data?.name || data?.result?.clientName || clientId
  const powerBIWorkspaceName = clientDetailQuery.data?.powerBIWorkspaceName?.trim()
  const comparableInitial = useMemo(() => JSON.stringify(toComparableRows(serverRows)), [serverRows])
  const comparableDraft = useMemo(() => JSON.stringify(toComparableRows(draftRows)), [draftRows])
  const hasChanges = comparableDraft !== comparableInitial || removedProjectStatus.length > 0
  const hasValidationErrors = !validateRows(draftRows)
  const completionPercent = useMemo(() => calculateCompletion(draftRows), [draftRows])
  const filteredRows = useMemo(() => {
    const query = nameFilter.trim().toLowerCase()
    if (!query) return draftRows
    return draftRows.filter((row) => row.item.toLowerCase().includes(query))
  }, [draftRows, nameFilter])
  const assignToOptions = useMemo(
    () => [
      { label: DASHBOARD_INSIGHTS_ASSIGNEE, value: DASHBOARD_INSIGHTS_ASSIGNEE },
      { label: clientName, value: clientName },
    ],
    [clientName]
  )

  const handleAddRow = () => {
    setDraftRows((current) => [
      ...current,
      {
        key: `new-${Date.now()}-${current.length}`,
        item: '',
        status: 'Pending',
        clientId,
        assignedTo: DASHBOARD_INSIGHTS_ASSIGNEE,
        dueDate: null,
        isCompleted: false,
        isNew: true,
        oldValue: '',
      },
    ])
  }

  const updateRow = (key: string, updates: Partial<ProjectStatusDraftRow>) => {
    setDraftRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row
        const next = { ...row, ...updates }
        if ('isCompleted' in updates) {
          next.status = next.isCompleted ? 'Completed' : 'Pending'
        }
        return next
      })
    )
  }

  const handleDeleteRow = useCallback((row: ProjectStatusDraftRow) => {
    Modal.confirm({
      title: 'Delete project status item?',
      content: row.item.trim() ? `"${row.item.trim()}" will be removed.` : 'This unsaved row will be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deleteMutation.isPending },
      onOk: async () => {
        if (row.isNew || !row.oldValue.trim()) {
          setDraftRows((current) => current.filter((entry) => entry.key !== row.key))
          return
        }

        const payload: ProjectStatusDeletePayload = {
          item: row.oldValue,
          clientId: row.clientId,
        }

        try {
          await deleteMutation.mutateAsync(payload)
          setRemovedProjectStatus((current) =>
            current.includes(row.oldValue) ? current : [...current, row.oldValue]
          )
          addToast('Project status item deleted', 'success')
        } catch (error) {
          addToast(error instanceof Error ? error.message : 'Failed to delete project status item', 'error')
        }
      },
    })
  }, [addToast, deleteMutation])

  const handleSave = async () => {
    if (hasValidationErrors || !hasChanges) return

    try {
      await upsertMutation.mutateAsync({
        projectStatus: buildUpsertRows(draftRows),
        removedProjectStatus,
      })
      addToast('The changes has been saved.', 'success')
      setRemovedProjectStatus([])
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save project status', 'error')
    }
  }

  const handleShare = async () => {
    const link = `${window.location.origin}/client/${clientId}/projectstatus`
    try {
      await copyText(link)
      addToast('Link has been copied', 'success')
    } catch {
      addToast('Failed to copy share link', 'error')
    }
  }

  const columns: ColumnsType<ProjectStatusDraftRow> = useMemo(() => {
    const baseColumns: ColumnsType<ProjectStatusDraftRow> = [
      {
        title: 'Item',
        dataIndex: 'item',
        key: 'item',
        render: (_, row) =>
          readOnly ? (
            row.item || '—'
          ) : (
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={row.item}
              onChange={(event) => updateRow(row.key, { item: event.target.value })}
            />
          ),
      },
      {
        title: 'Completed',
        dataIndex: 'isCompleted',
        key: 'isCompleted',
        width: 120,
        render: (_, row) => (
          <div className="flex justify-center">
            <Checkbox
              checked={row.isCompleted}
              disabled={readOnly}
              onChange={(event) => updateRow(row.key, { isCompleted: event.target.checked })}
            />
          </div>
        ),
      },
      {
        title: 'Assigned To',
        dataIndex: 'assignedTo',
        key: 'assignedTo',
        width: 220,
        render: (_, row) =>
          readOnly ? (
            row.assignedTo
          ) : (
            <Select
              value={row.assignedTo}
              options={assignToOptions}
              onChange={(value) => updateRow(row.key, { assignedTo: value })}
              className="w-full"
            />
          ),
      },
      {
        title: 'Due Date',
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: 180,
        render: (_, row) =>
          readOnly ? (
            row.dueDate ? dayjs(row.dueDate).format('DD-MM-YYYY') : '—'
          ) : (
            <DatePicker
              value={row.dueDate ? dayjs(row.dueDate) : null}
              format="DD-MM-YYYY"
              onChange={(value) => updateRow(row.key, { dueDate: value ? value.format('YYYY-MM-DD') : null })}
              className="w-full"
            />
          ),
      },
    ]

    if (!readOnly) {
      return [
        {
          title: '',
          key: 'delete',
          width: 64,
          render: (_, row) => (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete ${row.item || 'project status row'}`}
              onClick={() => handleDeleteRow(row)}
            />
          ),
        },
        ...baseColumns,
      ]
    }

    return baseColumns
  }, [assignToOptions, handleDeleteRow, readOnly])

  if (isBusy) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-white to-teal-50/40 shadow-card">
      <div className="flex items-start justify-between gap-4 border-b border-brand-100 px-6 py-5">
        <div>
          <Typography.Title level={2} className="!mb-1 !font-display !text-2xl !text-gray-900">
            {clientName}
          </Typography.Title>
          {!isPublic && powerBIWorkspaceName ? (
            <Typography.Text type="secondary">ID: {powerBIWorkspaceName}</Typography.Text>
          ) : null}
        </div>
        {!readOnly ? (
          <Space wrap>
            <Button icon={<PlusOutlined />} onClick={handleAddRow}>
              Add
            </Button>
            <Button icon={<ShareAltOutlined />} onClick={() => void handleShare()}>
              Share
            </Button>
            <Button
              type="primary"
              onClick={() => void handleSave()}
              disabled={!hasChanges || hasValidationErrors}
              loading={upsertMutation.isPending}
            >
              Save
            </Button>
          </Space>
        ) : null}
      </div>

      <div className="px-6 py-5">
        <div className="mx-auto mb-6 max-w-3xl">
          <Progress percent={completionPercent} strokeColor="#15B79E" />
          <Typography.Text className="mt-2 block text-center text-sm text-gray-600">
            {completionPercent}% Completed
          </Typography.Text>
        </div>

        <div className="mb-4 flex justify-end">
          <Input
            allowClear
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search by item name"
            className="w-full max-w-sm"
          />
        </div>

        {hasValidationErrors && !readOnly ? (
          <Alert className="mb-4" type="error" showIcon message="Items cannot be repeated or empty." />
        ) : null}

        {isError ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load project status"
            action={
              <Button size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <Table<ProjectStatusDraftRow>
            rowKey="key"
            columns={columns}
            dataSource={filteredRows}
            loading={isFetching || deleteMutation.isPending}
            pagination={pagination}
            onChange={(nextPagination) => setPagination((current) => ({ ...current, ...nextPagination }))}
            locale={{ emptyText: 'No project status items found' }}
          />
        )}
      </div>
    </div>
  )
}
